import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import {
  components,
  ControlProps,
  InputActionMeta,
  SingleValue,
  SingleValueProps,
  StylesConfig,
} from 'react-select';
import AsyncSelect from 'react-select/async';
import { useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { useRouter } from 'next/router';
import tailwindConfig from '../../../../tailwind.config';
import analytics from '../../../core/analytics/analytics';
import { ContentType, HOTEL_SEARCH_DEBOUNCE_DELAY_MS, Locale } from '../../../core/constants';
import useIsAboveBreakpoint from '../../../core/hooks/useIsAboveBreakpoint';
import Location from '../Icons/Location';
import getSearchAutocomplete from '../../../core/hotel/getSearchAutocomplete';
import Property from '../Icons/Property';
import HotelSearchSelectOption from '../../../../types/HotelSearchSelectOption';
import Loading from '../../../../public/animations/loading.json';
import Lottie from '../Lottie/Lottie';
import SearchInput from './SearchInput';
import SearchOption from './SearchOption';
import useHotelSearchOptions from '../../../core/hotel/useHotelSearchOptions';

const Modal = dynamic(() => import('../Modal/Modal'));
const CustomSingleValue = (
  props: SingleValueProps<
    HotelSearchSelectOption & {
      prefecture?: { ja: string; en: string };
      prefectureGroup?: { ja: string; en: string };
    }
  >
) => {
  const router = useRouter();
  const isLocaleJA = router.locale === 'ja';
  const label = isLocaleJA ? props.data.label.ja : props.data.label.en;
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <components.SingleValue {...props}>{label}</components.SingleValue>;
};

const CustomControl = ({ children, ...props }: ControlProps<HotelSearchSelectOption, false>) => {
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <components.Control {...props}>
      <Image
        src="/magnifying_glass.svg"
        alt="magnifying_glass"
        width={17}
        height={17}
        className="ml-4"
      />
      {children}
    </components.Control>
  );
};

type Props = {
  id: string;
  dataTestId?: string;
  searchTerm?: SingleValue<HotelSearchSelectOption>;
  onSearchTermChange: (newValue: SingleValue<HotelSearchSelectOption>) => void;
  roundedSearchBar?: boolean;
};

const SearchDropdown = ({
  id,
  dataTestId,
  searchTerm,
  onSearchTermChange,
  roundedSearchBar,
}: Props) => {
  const { t } = useTranslation('common');
  const isAboveLG = useIsAboveBreakpoint('LG');
  const isAboveMD = useIsAboveBreakpoint('MD');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const router = useRouter();
  const isLocaleJA = router.locale === Locale.JAPANESE;

  const { searchInputValue, setSearchInputValue, defaultOptions, options } =
    useHotelSearchOptions();
  const [searchValue, setSearchValue] = useState('');
  const [lastSearchValue, setLastSearchValue] = useState(searchValue);
  const [searchOptions, setSearchOptions] = useState<HotelSearchSelectOption[]>(defaultOptions);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const loadOptions = useMemo(
    () =>
      debounce((inputValue: string, callback: (options: HotelSearchSelectOption[]) => void) => {
        // Prevent API call if input is empty
        if (!inputValue) {
          setSearchOptions(defaultOptions);
          callback([]);
          return;
        }

        getSearchAutocomplete({ term: inputValue })
          .then((result) => {
            const formattedOptions = result.data.map((item) => {
              const { type } = item;

              if (type === 'property') {
                return {
                  value: item.propertyId,
                  label: item.name,
                  type,
                  prefecture: item.prefecture,
                  prefectureGroup: item.prefectureGroup,
                };
              }

              if (type === 'city') {
                return {
                  type,
                  value: `${item.location.lat},${item.location.lon}`,
                  label: item.cityName,
                  prefecture: item.prefectureName,
                  location: item.location,
                };
              }

              return {
                value: item.id,
                label: item.name,
                type,
              };
            });
            setSearchOptions(formattedOptions);
            callback(formattedOptions);
          })
          .catch((error) => {
            console.error('Error fetching search autocomplete options:', error);
            setSearchOptions([]);
            callback([]);
          });
      }, HOTEL_SEARCH_DEBOUNCE_DELAY_MS),
    [defaultOptions]
  );

  const formatOptionLabel = ({
    value,
    label,
    type,
    prefecture,
    prefectureGroup,
  }: HotelSearchSelectOption & {
    prefecture?: {
      ja: string;
      en: string;
    };
    prefectureGroup?: {
      ja: string;
      en: string;
    };
  }) => {
    const labelText = isLocaleJA ? label.ja : label.en;
    const isSelected = searchTerm?.value === value;

    const getDesc = () => {
      switch (type) {
        case 'city':
          return isLocaleJA ? prefecture?.ja : prefecture?.en;
        case 'prefecture':
          return t('prefecture');
        case 'property':
          return isLocaleJA
            ? `${prefecture?.ja}, ${prefectureGroup?.ja}`
            : `${prefecture?.en}, ${prefectureGroup?.en}`;
        default:
          return '';
      }
    };

    return (
      <div className="flex items-center gap-2">
        <div className="rounded-md bg-gray-100 p-2">
          {type === 'property' ? (
            <Property size={20} color={tailwindConfig.theme.colors.gray[300]} />
          ) : (
            <Location size={20} color={tailwindConfig.theme.colors.gray[300]} />
          )}
        </div>

        <div className="flex flex-col">
          <span className="line-clamp-1">{labelText}</span>
          <span className={classNames('text-xs capitalize', !isSelected && 'text-gray-500')}>
            {getDesc()}
          </span>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (isAboveMD) {
      setIsModalOpen(false);
    }
  }, [isAboveMD, setIsModalOpen]);

  const colourStyles: StylesConfig<HotelSearchSelectOption, false> = {
    control: (styles) => ({
      ...styles,
      height: isAboveLG || roundedSearchBar ? '56px' : '48px',
      backgroundColor: roundedSearchBar ? 'transparent' : tailwindConfig.theme.colors.gray[50],
      borderStyle: roundedSearchBar ? 'none' : tailwindConfig.theme.colors.gray[200],
      borderRadius: roundedSearchBar ? '13px' : '3px',
      cursor: 'pointer',
      ':hover': {
        borderStyle: roundedSearchBar ? 'none' : 'solid',
        borderColor: tailwindConfig.theme.colors.gray[300],
      },
      boxShadow: 'none',
    }),
    container: (styles) => ({
      ...styles,
      width: '100%',
    }),
    clearIndicator: (styles) => ({
      ...styles,
      color: roundedSearchBar
        ? tailwindConfig.theme.colors.gray[950]
        : tailwindConfig.theme.colors.gray[600],
      ':hover': {
        color: roundedSearchBar
          ? tailwindConfig.theme.colors.gray[800]
          : tailwindConfig.theme.colors.gray[500],
      },
    }),
    placeholder: (styles) => ({
      ...styles,
      color: roundedSearchBar
        ? tailwindConfig.theme.colors.gray[400]
        : tailwindConfig.theme.colors.gray[600],
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }),
    menu: (styles) =>
      roundedSearchBar
        ? {
            ...styles,
            marginTop: '16px',
            borderRadius: '16px',
            overflowY: 'auto',
            width: '480px',
          }
        : styles,
    menuList: (styles) =>
      roundedSearchBar
        ? {
            ...styles,
            borderRadius: '16px',
            margin: '4px 0',
            scrollbarColor: `${tailwindConfig.theme.colors.scrollbarColor} ${tailwindConfig.theme.colors.transparent}`,
            scrollbarWidth: 'thin',
            padding: '8px 0',
          }
        : styles,
    option: (styles) =>
      roundedSearchBar
        ? {
            ...styles,
            padding: '8px 24px',
          }
        : styles,
    indicatorsContainer: (styles) => ({
      ...styles,
      display: 'none',
    }),
  };

  const noOptionsMessage = () => <p>{t('search_header.no_options')}</p>;
  const loadingMessage = () => (
    <Lottie
      style={{ width: 90, height: 90, margin: '0 auto' }}
      params={{
        loop: true,
        animationData: Loading,
      }}
    />
  );
  const label = isLocaleJA ? searchTerm?.label.ja : searchTerm?.label.en;

  const events = {
    onChange: (value: SingleValue<HotelSearchSelectOption>) => {
      onSearchTermChange(value);

      if (isModalOpen) setIsModalOpen(false);
      if (isMenuOpen) setIsMenuOpen(false);

      analytics.google.basic.track.select(ContentType.SEARCH_DROPDOWN, value?.value || '');
    },
    onMenuOpen: () => {
      if (lastSearchValue) {
        setSearchValue(lastSearchValue);
      } else if (searchTerm && label && searchValue !== label) {
        setSearchValue(label);
        loadOptions(label, () => {});
      }
      setIsMenuOpen(true);
    },
    onBlur: () => {
      setLastSearchValue(searchValue);
      setSearchValue('');
      setIsMenuOpen(false);
    },
    onInputChange: (newValue: string, { action }: InputActionMeta) => {
      if (action === 'input-change') {
        setSearchValue(newValue);
      }
      return newValue;
    },
  };

  return isAboveMD ? (
    <AsyncSelect
      aria-label="Search Dropdown"
      instanceId={id} // instanceId needs to be assigned since we have a case where multiple Select components in DOM
      data-testid={dataTestId}
      value={searchTerm}
      loadOptions={loadOptions}
      defaultOptions={searchValue ? searchOptions : defaultOptions}
      inputValue={searchValue}
      controlShouldRenderValue={!isMenuOpen}
      isSearchable
      blurInputOnSelect
      onMenuOpen={events.onMenuOpen}
      onBlur={events.onBlur}
      onChange={events.onChange}
      menuIsOpen={isMenuOpen}
      onInputChange={events.onInputChange}
      placeholder={t('search_header.search_hotel_placeholder')}
      noOptionsMessage={noOptionsMessage}
      styles={colourStyles}
      theme={(theme) => ({
        ...theme,
        colors: {
          ...theme.colors,
          primary: tailwindConfig.theme.colors.orange[800],
          primary75: tailwindConfig.theme.colors.red[600],
          primary50: tailwindConfig.theme.colors.red[500],
          primary25: tailwindConfig.theme.colors.red[200],
        },
      })}
      formatOptionLabel={formatOptionLabel}
      components={{
        SingleValue: CustomSingleValue,
        Control: CustomControl,
      }}
      loadingMessage={loadingMessage}
    />
  ) : (
    <>
      <button
        className={classNames(
          'flex gap-4 items-center px-5 w-full h-12 bg-gray-50 rounded border border-gray-200 hover:border-gray-300 cursor-pointer',
          { 'gap-2 h-14 border-none bg-white': roundedSearchBar }
        )}
        type="button"
        onClick={() => setIsModalOpen(true)}
        data-testid={dataTestId}
      >
        {roundedSearchBar && (
          <Image src="/magnifying_glass.svg" alt="magnifying_glass" width={17} height={17} />
        )}
        <p className="truncate">
          {label ||
            (roundedSearchBar
              ? t('search_header.search_hotel')
              : t('search_header.please_select_prefecture'))}
        </p>
      </button>
      {isModalOpen && (
        <Modal title={t('search_header.search_hotel')} onClose={() => setIsModalOpen(false)}>
          <SearchInput
            onChange={(e) => setSearchInputValue(e.target.value)}
            value={searchInputValue}
            onClear={() => setSearchInputValue('')}
          />
          {options?.length === 0 && (
            <div className="flex h-14 items-center justify-center">
              <p className="text-gray-500">{t('search_header.no_options')}</p>
            </div>
          )}
          {options?.map((option) => (
            <SearchOption
              key={option.value}
              option={option}
              isSelected={searchTerm?.value === option.value}
              isLocaleJA={isLocaleJA}
              onClick={() => events.onChange(option)}
            />
          ))}
        </Modal>
      )}
    </>
  );
};

export default SearchDropdown;
